import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';
import { unsubscribeComponent } from '../../../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { DateHelpers } from '../../../../../shared/services/date-helpers/date-helpers.service';
import { IAssessmentInputRow } from '../assessments-data-input.component';
import { AssessmentsDataInputService } from '../assessments-data-input.service';
@unsubscribeComponent
@Component({
  selector: 'assessments-data-input-body',
  template: require('./assessments-data-input-body.component.html'),
  styles: [require('./assessments-data-input-body.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssessmentsDataInputBodyComponent implements OnInit, AfterViewInit {
  // https://stackoverflow.com/questions/34947154/angular-2-viewchild-annotation-returns-undefined
  @ViewChildren(CdkVirtualScrollViewport) scrollViewport: [CdkVirtualScrollViewport];
  @Input() filterForm: FormControl;
  @Input() sort$: BehaviorSubject<{ key: string; dir: string }>;
  @Input() currentGroupingData$: BehaviorSubject<IAssessmentInputRow[]>;
  @Input() assessmentsForm: FormGroup;
  @Input() originalFormControls: { [key: string]: FormGroup };
  @Output() onScroll = new EventEmitter<HTMLElement>();
  @Output() onClickStudentRow = new EventEmitter<string>();
  @Output() countOfRowDataChanged = new EventEmitter<number>();
  dataSource$ = new BehaviorSubject<any[]>([]);
  dataSourceSub: Subscription;
  viewportScrollingSub: Subscription;
  today: string;

  constructor(private assmntsDataInputSrv: AssessmentsDataInputService, private dateHelpers: DateHelpers) {}

  ngOnInit() {
    this.today = this.dateHelpers.getFormattedNow('YYYY-MM-DD');
    this.dataSourceSub = combineLatest(
      this.currentGroupingData$,
      this.filterForm.valueChanges.pipe(startWith('')),
      this.sort$,
    )
      .pipe(
        tap(([currentGroupingData, filterTerm, sort]) => {
          const filteredRowData = this.assmntsDataInputSrv.filterGroupings({
            rowData: currentGroupingData,
            filterTerm,
          });
          const sortedRowData = this.assmntsDataInputSrv.sortGroupings({
            rowData: filteredRowData,
            sort,
          });

          let data: any = sortedRowData;

          // If we are reusing the same assessments, maintain the original values
          // to prevent multiple users overwriting each others in progress data
          // Otherwise, keep data set to sortedRowData
          const assessmentsAreEqual = this.assessmentsAreEqual(sortedRowData);
          if (assessmentsAreEqual) {
            data = sortedRowData.map(
              (row: any, k): Observable<{}> => {
                if (this.assessmentsForm.controls[row.studentId].dirty === false) {
                  return row;
                } else {
                  row.instructionalScore = this.assessmentsForm.controls[row.studentId].value.instructional;
                  row.independentScore = this.assessmentsForm.controls[row.studentId].value.independent;
                  row.assessmentDate = this.assessmentsForm.controls[row.studentId].value.assessmentDate;
                  return row;
                }
              },
            );
          }
          this.dataSource$.next(data);
          this.countOfRowDataChanged.emit(data.length);
        }),
      )
      .subscribe();
  }

  ngAfterViewInit() {
    if (this.scrollViewport) {
      this.viewportScrollingSub = (this.scrollViewport as any)._results[0].elementScrolled().subscribe(e => {
        this.onScroll.emit(e.target as HTMLElement);
      });
    }
  }

  assessmentsAreEqual(sortedRowData: IAssessmentInputRow[]): boolean {
    if (!this.assessmentsForm || !this.assessmentsForm.controls) return false;

    for (const { studentId } of sortedRowData) {
      if (!this.assessmentsForm.controls[studentId]) {
        return false;
      }
    }
    return true;
  }

  trackByFn(i, item) {
    return item.studentAssessmentId;
  }

  studentRowClicked($studentId) {
    this.onClickStudentRow.emit($studentId);
  }

  clearSearchBtnClicked(): void {
    this.filterForm.setValue('');
  }
}
